// Hunger, moods and feeding (docs/04 §12, §9). Hunger and moods exist once the machine has a
// face (G_GROW_1); feeding needs G_FEED.
import { B } from '../content/balance';
import { FED_SECRETS } from '../content/secrets';
import { SYMBOLS } from '../content/registry';
import type { Decimal } from './decimal';
import { say, tip } from './dialogue';
import { removeFromStrips } from './grid';
import type { Modifiers } from './modifiers';
import { stream, type Rng } from './rng';
import { par, priceOf, spinBudget, tribute } from './tribute';
import type { Cell, Expression, GameEvent, GameState, HungerMood, MoodId, MoodState, Rarity, RunState, SymbolInstance } from './types';
import { clamp } from './util';

export function newMood(): MoodState {
  return { temp: null, left: 0, calmStreak: 0, adoringNext: false };
}

export function hungerActive(mods: Modifiers): boolean {
  return mods.features.has('face');
}

export function hungerMood(hunger: number): HungerMood {
  if (hunger >= B.HUNGER_MAX) return 'starving';
  if (hunger >= B.MOOD_RAVENOUS) return 'ravenous';
  if (hunger >= B.MOOD_PECKISH) return 'peckish';
  return 'content';
}

/** The one active mood: a temporary mood overrides the hunger mood while it lasts. */
export function moodOf(run: RunState, mods: Modifiers): MoodId {
  if (!hungerActive(mods)) return 'content';
  return run.mood.temp ?? hungerMood(run.hunger);
}

export const MOOD_FACE: Record<MoodId, Expression> = {
  content: 'neutral',
  peckish: 'hungry',
  ravenous: 'ravenous',
  starving: 'starving',
  amused: 'laughing',
  sulking: 'sulking',
  adoring: 'adoring',
};

export function moodLuck(run: RunState, mods: Modifiers): number {
  switch (moodOf(run, mods)) {
    case 'peckish':
      return B.PECKISH_LUCK;
    case 'amused':
      return B.AMUSED_LUCK;
    case 'content':
      return hungerActive(mods) ? mods.contentLuck : 0;
    default:
      return 0;
  }
}

/** ×MULT sources from the mood and from Glutton's Gift. */
export function moodXMults(run: RunState, mods: Modifiers): { source: string; x: number }[] {
  const out: { source: string; x: number }[] = [];
  const mood = moodOf(run, mods);
  if (mood === 'ravenous') out.push({ source: 'ravenous', x: mods.ravenousX });
  if (mood === 'starving') out.push({ source: 'starving', x: B.STARVING_XMULT });
  const eats = run.window.counters.eats ?? 0;
  if (mods.eatXMult > 0 && eats > 0) out.push({ source: 'gluttonsGift', x: 1 + mods.eatXMult * eats });
  return out;
}

/** Sets Hunger (clamped) and reports a HUNGER event when the value or the mood changed. */
export function setHunger(state: GameState, mods: Modifiers, value: number, events: GameEvent[]): void {
  const run = state.run!;
  if (!hungerActive(mods)) return;
  const prevMood = moodOf(run, mods);
  const prev = run.hunger;
  const cap = Math.min(B.HUNGER_MAX, mods.hungerCap);
  run.hunger = clamp(Math.round(value * 100) / 100, Math.min(mods.hungerFloor, cap), cap);
  const mood = moodOf(run, mods);
  if (run.hunger === prev && mood === prevMood) return;
  events.push({ e: 'HUNGER', value: run.hunger, mood, prevMood });
  if (mood === prevMood) return;
  if (mood === 'peckish' && hungerMood(prev) === 'content') say(state, 'peckish', events);
  else if (mood === 'ravenous') say(state, 'ravenous', events, undefined, { force: true });
  else if (mood === 'starving') say(state, 'starving', events, undefined, { force: true });
}

/** Step 9: Hunger rises every spin. */
export function hungerTick(state: GameState, mods: Modifiers, events: GameEvent[]): void {
  const run = state.run!;
  if (!hungerActive(mods)) return;
  tip(state, 'hunger', events);
  const base = run.window.reckoning ? B.HUNGER_PER_RECKONING_SPIN : B.HUNGER_PER_SPIN;
  setHunger(state, mods, run.hunger + base * Math.max(0, 1 + mods.hungerGainPct) * mods.hungerGainX + mods.hungerPerSpin, events);
}

export function setTempMood(state: GameState, mods: Modifiers, temp: MoodState['temp'], left: number, events: GameEvent[]): void {
  const run = state.run!;
  if (!hungerActive(mods)) return;
  const prevMood = moodOf(run, mods);
  run.mood.temp = temp;
  run.mood.left = left;
  const mood = moodOf(run, mods);
  if (mood !== prevMood) events.push({ e: 'HUNGER', value: run.hunger, mood, prevMood });
}

/** At every pull: AMUSED counts down. */
export function moodSpinTick(state: GameState, mods: Modifiers, events: GameEvent[]): void {
  const m = state.run!.mood;
  if (m.temp && m.left > 0) {
    m.left--;
    if (m.left === 0) setTempMood(state, mods, null, 0, events);
  }
}

/** Window end: SULKING/ADORING expire; calm windows build towards ADORING. */
export function moodWindowEnd(state: GameState, mods: Modifiers, events: GameEvent[]): void {
  const run = state.run!;
  if (!hungerActive(mods)) return;
  if (run.mood.temp && run.mood.left < 0) setTempMood(state, mods, null, 0, events);
  run.mood.calmStreak = run.hunger < B.MOOD_PECKISH ? run.mood.calmStreak + 1 : 0;
  if (run.mood.calmStreak >= mods.adoringWindows) {
    run.mood.calmStreak = 0;
    run.mood.adoringNext = true;
  }
}

/** Window start: an owed ADORING mood grants +1 Hold and +1 Nudge. Returns the extra charges. */
export function moodWindowStart(state: GameState, mods: Modifiers, events: GameEvent[]): { hold: number; nudge: number } {
  const run = state.run!;
  if (!hungerActive(mods) || !run.mood.adoringNext) return { hold: 0, nudge: 0 };
  run.mood.adoringNext = false;
  setTempMood(state, mods, 'adoring', -1, events);
  say(state, 'adoring', events, undefined, { force: true });
  return { hold: mods.features.has('hold') ? 1 : 0, nudge: mods.features.has('nudge') ? 1 : 0 };
}

export function holdCost(run: RunState, mods: Modifiers): number {
  return moodOf(run, mods) === 'sulking' ? B.SULK_HOLD_COST : 1;
}

// ── Eating (RAVENOUS / STARVING) ─────────────────────────────────────

export interface EatCandidate {
  uid: number;
  cell: Cell;
  value: number;
}

/**
 * Chooses what the machine eats this spin, or null. RAVENOUS: a random visible symbol with a
 * chance per spin. STARVING: the highest-value one, every spin. Picky Eater: the lowest-value one.
 */
export function pickMeal(run: RunState, mods: Modifiers, cands: EatCandidate[], rng: Rng): EatCandidate | null {
  const mood = moodOf(run, mods);
  if (mood !== 'ravenous' && mood !== 'starving') return null;
  if (run.deck.length <= B.MIN_DECK || cands.length === 0) return null;
  if (mood === 'ravenous' && !rng.chance(B.RAVENOUS_EAT_CHANCE)) return null;
  if (mood === 'ravenous' && !mods.pickyEater) return rng.pick(cands);
  const sorted = [...cands].sort((a, b) => (mods.pickyEater ? a.value - b.value : b.value - a.value));
  return sorted[0]!;
}

// ── Feeding (intermission) ───────────────────────────────────────────

const TIER: Record<Rarity, number> = { C: 1, U: 2, R: 3, L: 4 };

function nextPar(run: RunState, mods: Modifiers): Decimal {
  const n = run.window.n + 1;
  return par(tribute(n, mods), spinBudget(n, mods, run.nextWindow.spins));
}

export function feedCoinsPrice(run: RunState, mods: Modifiers): Decimal {
  return priceOf(B.FEED_COINS_PRICE, tribute(run.window.n + 1, mods));
}

export function feedAmount(inst: SymbolInstance, mods: Modifiers): number {
  const rarity = SYMBOLS.get(inst.defId)?.rarity ?? 'C';
  return B.FEED_HUNGER[rarity] * (1 + mods.feedPct) * mods.feedX;
}

/** Charm and relic riders on any feeding (Second Mouth, Chalice of Plenty). */
function afterFeed(state: GameState, mods: Modifiers): void {
  const run = state.run!;
  const im = run.intermission!;
  if (mods.feedDraftBonus) run.flags.draftBonus = 1;
  if (mods.feedSpin && !im.flags.feedSpin) {
    im.flags.feedSpin = 1;
    run.nextWindow.spins++;
  }
}

function canFeed(state: GameState, mods: Modifiers): string | undefined {
  const run = state.run;
  if (!run || run.phase !== 'intermission') return 'not in intermission';
  if (!mods.features.has('feed') || !hungerActive(mods)) return 'feeding locked';
  return undefined;
}

export function feedSymbol(state: GameState, mods: Modifiers, uid: number, events: GameEvent[]): string | undefined {
  const err = canFeed(state, mods);
  if (err) return err;
  const run = state.run!;
  if (run.deck.length <= B.MIN_DECK) return 'deck too small';
  const idx = run.deck.findIndex((d) => d.uid === uid);
  if (idx < 0) return 'no such symbol';
  const inst = run.deck[idx]!;
  const def = SYMBOLS.get(inst.defId);
  const amount = feedAmount(inst, mods);
  run.deck.splice(idx, 1);
  removeFromStrips(run, uid);
  events.push({ e: 'SYMBOL_FED', uid, defId: inst.defId, amount });
  const p = nextPar(run, mods);
  const value = Math.max(0, (def?.baseValue ?? 0) + inst.bonusValue);
  const coins = p.mul(value * mods.feedValuePar + TIER[def?.rarity ?? 'C'] * mods.feedTierPar).floor();
  if (coins.gt(0)) {
    run.coins = run.coins.add(coins);
    events.push({ e: 'CHARM_TRIGGER', id: 'feed', kind: 'coins', amount: coins.toString() });
  }
  // Surgeon's Kit: a random deck symbol grows.
  if (mods.feedGrow > 0 && run.deck.length) {
    const target = run.deck[stream(run.rng, 'events').int(run.deck.length)]!;
    target.bonusValue += mods.feedGrow;
    events.push({ e: 'SYMBOL_GROWN', uid: target.uid, defId: target.defId, delta: mods.feedGrow, by: 'surgeonkit' });
  }
  const fed = state.meta.lifetime.fed;
  fed[inst.defId] = (fed[inst.defId] ?? 0) + 1;
  // Secrets (docs/06 §8): e.g. the 77th Cherry.
  for (const secret of FED_SECRETS) {
    if (secret.defId !== inst.defId || (fed[inst.defId] ?? 0) < secret.count || state.meta.flags[secret.flag]) continue;
    state.meta.flags[secret.flag] = true;
    events.push({ e: 'SAY', key: secret.line, channel: 'display', expression: 'sulking' });
  }
  setHunger(state, mods, run.hunger - amount, events);
  afterFeed(state, mods);
  say(state, 'fed', events);
  return undefined;
}

export function feedCoins(state: GameState, mods: Modifiers, events: GameEvent[]): string | undefined {
  const err = canFeed(state, mods);
  if (err) return err;
  const run = state.run!;
  if (run.hunger <= 0) return 'not hungry';
  const price = feedCoinsPrice(run, mods);
  if (run.coins.lt(price)) return 'not enough coins';
  run.coins = run.coins.sub(price);
  setHunger(state, mods, run.hunger - B.FEED_COINS_HUNGER * (1 + mods.feedPct) * mods.feedX, events);
  afterFeed(state, mods);
  say(state, 'fed', events);
  return undefined;
}
